import {
  boardAddressSchema,
  boardContentsSchema,
  cardPath,
  cardRefFor,
  slugify,
  type BoardAddress,
  type BoardContents,
  type BoardContentsBoard,
  type BoardRole,
} from '@molt/shared';
import { queryOptions } from '@tanstack/react-query';
import { get } from './api';
import {
  EMPTY_BOARD_FILTER,
  boardFilterCacheKey,
  boardFilterSearchParams,
  type BoardFilterSelection,
} from './board-filter';
import { queryKeys, type MoltQueryKey } from './query-keys';

/**
 * The board face's data layer — `FE-13.1`, `C1-BRIEF.md` §7.1.
 *
 * ---------------------------------------------------------------------------
 * TWO REQUESTS, AND WHY THEY CANNOT BE COLLAPSED INTO ONE QUERY
 * ---------------------------------------------------------------------------
 * The URL a member holds is `/b/<shortLink>/<slug>` and the payload the face
 * draws is `GET /boards/:id/contents`, so opening a board is always resolve →
 * fetch. The obvious economy — one `useQuery` whose `queryFn` does both — is
 * **wrong**, and the reason is a silent failure rather than a slow one:
 *
 *   `lib/query-keys.ts`'s `eventBlastRadius` reaches the board face by the
 *   prefix `['boards', boardId]`, and the only entry under it that the face
 *   reads is `['boards', boardId, 'contents']`. A combined query would have to
 *   be keyed by the **short link**, which no live frame carries (a frame has
 *   `boardId` and `cardId`, nothing else — see that file's recorded limit). The
 *   board would be correct on load and permanently stale over the socket, with
 *   nothing red anywhere.
 *
 * So: two queries, the second `enabled` once the first has an id, and the
 * contents entry lands on `queryKeys.boards.contents(boardId)` exactly.
 *
 * ---------------------------------------------------------------------------
 * WHAT THIS FILE DELIBERATELY DOES NOT DO
 * ---------------------------------------------------------------------------
 *  - **No route strings in `lib/api.ts`.** That file's own comment says a block
 *    spells its path in its own file; this is that file.
 *  - **No branching on the error code.** A board that does not exist and a
 *    board the caller may not see are the same 404 by design (`CLAUDE.md`, "A
 *    caller outside a read scope gets 404"), and telling them apart in the
 *    client would reintroduce the existence oracle the server refuses to be.
 *  - **No `unauthenticated` handling.** That redirect is `FE-06.1`'s and is
 *    bound once, globally, against `isUnauthenticated`.
 */

/**
 * Resolve `/b/<shortLink>/…` to a board — `GET /boards/by-short-link/:shortLink`,
 * `FR-EXP-015`.
 *
 * ---------------------------------------------------------------------------
 * ⚠ `boardAddressSchema`, NOT `boardSchema` — A DELIBERATE DEPARTURE FROM
 *    `C1-BRIEF.md` §7.1, RECORDED HERE BECAUSE IT LOOKS LIKE A MISTAKE
 * ---------------------------------------------------------------------------
 * The brief spells this `Promise<Board>` parsed with `boardSchema`. The route
 * actually answers **`boardAddressSchema`** (`apps/server/src/routes/boards.ts`,
 * `response: { 200: boardAddressSchema }`), which is `boardSchema` extended with
 * `path` — the canonical `/b/<shortLink>/<slug>` the server assembles so the URL
 * grammar has one producer.
 *
 * `boardSchema` is a plain `z.object`, so parsing the response with it would
 * **strip `path` silently**: no error, no warning, and the one field
 * `FE-08.4`'s slug canonicalisation exists to consume is gone before any caller
 * sees it. Widening the schema is therefore not a convenience — it is the
 * difference between D3 extending this file and D3 having to re-fetch.
 *
 * The brief's signature is still honoured where it matters: `BoardAddress` is
 * `Board` plus one field, so `Promise<BoardAddress>` satisfies every consumer
 * typed for `Promise<Board>`. (Parsing with the wider schema while *declaring*
 * the narrower type was rejected — that carries `path` at runtime and hides it
 * from the type, which is worse than either honest option.)
 */
export function getBoardByShortLink(shortLink: string): Promise<BoardAddress> {
  return get(
    `/boards/by-short-link/${encodeURIComponent(shortLink)}`,
    boardAddressSchema,
  );
}

/** The whole board face in one payload — `GET /boards/:id/contents`, `BE-13.1`. */
export function getBoardContents(
  boardId: string,
  filter: BoardFilterSelection = EMPTY_BOARD_FILTER,
): Promise<BoardContents> {
  const params = boardFilterSearchParams(filter);
  const query = params.toString();
  return get(
    `/boards/${encodeURIComponent(boardId)}/contents${query === '' ? '' : `?${query}`}`,
    boardContentsSchema,
  );
}

/**
 * The resolver key. It mirrors the URL's literal path segment rather than using
 * `detail(shortLink)`: the resolver returns `boardAddressSchema`, while
 * `GET /boards/:id` returns a board row. One cache spelling for both would let
 * whichever request resolves last decide the shape stored under that entry.
 */
const boardByShortLinkKey = (shortLink: string): MoltQueryKey =>
  queryKeys.boards.byShortLink(shortLink);

/**
 * The resolver query. Its data is the board **address**; the face is drawn from
 * `boardContentsQueryOptions` below, never from this.
 */
export function boardByShortLinkQueryOptions(shortLink: string) {
  return queryOptions({
    queryKey: boardByShortLinkKey(shortLink),
    queryFn: () => getBoardByShortLink(shortLink),
  });
}

/**
 * The board face's own entry — `['boards', boardId, 'contents']`.
 *
 * This is the key `FE-04.16`'s socket client invalidates by prefix, so it must
 * come from `queryKeys.boards.contents` and from nowhere else.
 */
export function boardContentsQueryOptions(
  boardId: string,
  filter: BoardFilterSelection = EMPTY_BOARD_FILTER,
) {
  const filterKey = boardFilterCacheKey(filter);
  return queryOptions({
    queryKey: queryKeys.boards.contents(boardId, filterKey),
    queryFn: () => getBoardContents(boardId, filter),
  });
}

/**
 * Who is looking at this board — the parameter `canEdit` grew in `D4`.
 *
 * ⚠ **`rolesKnown` is the members query *and* the session, not the members
 * query alone.** `ownBoardRole(members, undefined)` returns `null`, the very
 * value it returns for *"you hold no row on this board"*, so a members-only
 * gate is a wrong answer rather than a slow one. Same spelling as
 * `BoardMenuPanel`'s and `BoardFace`'s, deliberately.
 */
export interface BoardViewer {
  /** `profile !== null` from `useSession()`. Never `!isSignedOut`. */
  signedIn: boolean;
  /** `ownBoardRole(members.data, profile?.id)` — `null` when the caller holds no row. */
  role: BoardRole | null;
  /** The members query has settled **and** the session is known. */
  rolesKnown: boolean;
  /**
   * `isSuperadminSession(session)` — SA-9. Required rather than optional so a
   * new call site cannot silently lock a platform superadmin out of a board the
   * server lets them edit.
   */
  isSuper: boolean;
}

/**
 * **The single permission seam** — `C1-BRIEF.md` §3, fact one.
 *
 * Every affordance on the board face is gated on this one boolean, computed in
 * this one place. A component that tests `board.closed` itself, or that renders
 * an affordance ungated, is the bug this function exists to prevent. Content
 * affordances (add a card, add a list, rename, quick-edit, archive-a-list,
 * drag) are **absent**, never disabled, for a non-editor — `D4-BRIEF.md` §2.1:
 * this is `FR-PERM-010` AC1 and `FR-EXP-034`'s edge case read literally, and it
 * is also the product tie-breaker (a board with a dozen disabled buttons no
 * longer reads in ten seconds). The board **menu**'s settings surface is a
 * different file's job and follows the opposite rule — locked, never hidden.
 *
 * ---------------------------------------------------------------------------
 * THE BRANCHES, ONE REQUIREMENT EACH
 * ---------------------------------------------------------------------------
 *  1. `board.closed` → `false`, unconditionally. `FR-EXP-012` AC2: a closed
 *     board renders read-only for every member, admin included.
 *  2. `!viewer.signedIn` → `false`. `FR-PERM-010` AC1: a signed-out visitor
 *     "can read lists, cards and comments but see no edit affordances."
 *  2a. `viewer.isSuper` → `true`. SA-5/SA-9: `requireBoardWriter` lets
 *     `access.isSuper` through each of its three refusals — no row, observer,
 *     self-join off — so a platform superadmin edits a board of any visibility.
 *     After the two branches above, because closure (D11) and a session both
 *     still bind them; before the rest, because none of the rest does.
 *  3. `!viewer.rolesKnown` → **`true` — fails OPEN.** See "THE FAIL-OPEN
 *     ASYMMETRY" below; this is the branch most likely to look backwards.
 *  4. `viewer.role === 'observer'` → `false`. `FR-PERM-016`: an observer
 *     "cannot create, edit, move or archive cards." (Residual 3 below is the
 *     one case this branch gets wrong, and no reordering fixes it.)
 *  5. `viewer.role !== null` (`'admin' | 'normal'`) → `true`. `FR-PERM-015`
 *     AC4: a normal board member has full content rights and a dragged card
 *     "succeeds."
 *  6. `viewer.role === null` → `board.visibility === 'private' ||
 *     board.visibility === 'workspace_visible'`. **The branch to read twice —
 *     corrected once already; read "WHY BRANCH 6 IS WHAT IT IS" below before
 *     touching it again.**
 *
 * ---------------------------------------------------------------------------
 * WHY BRANCH 6 IS WHAT IT IS — THE READ ITSELF IS THE EVIDENCE
 * ---------------------------------------------------------------------------
 * `role === null` means "no `board_members` row for this viewer." Four
 * populations share that fact, one per wire value of `board.visibility`, and
 * each is discriminated not by anything this predicate can see directly but by
 * *which arm of the server's read scope must have admitted them* — because the
 * caller is looking at this board's contents at all. `isBoardReadableBy`
 * (`apps/server/src/boards/permissions.ts`) has four arms: (a) on the board,
 * (b) admin of the board's workspace, (c) member of the board's workspace when
 * `visibility` is `'workspace_visible'`, (d) anyone at all when `visibility` is
 * `'public'` (plus an enterprise arm, symmetric with (c)). A `role === null`
 * viewer failed arm (a) by definition, so:
 *
 * (The platform superadmin arm is not among these, and it does not need to be:
 * branch 2a returns before this one, so every viewer the walk-through below
 * reasons about is known not to be a superadmin — SA-9.)
 *
 *   - **`private`** — only arms (a) and (b) can admit a reader of a private
 *     board; (c) and (d) both require a different `visibility`. So a
 *     `role === null` viewer reading a private board was admitted by arm (b)
 *     alone: **they are necessarily a workspace admin.** `requireBoardWriter`
 *     admits any caller with `workspaceRole === 'admin'` unconditionally (its
 *     checks all require `workspaceRole !== 'admin'` to fire), so this viewer
 *     can write. `FR-PERM-017` AC1 names the population directly: a workspace
 *     admin "can read [a private board] and change its settings." **True, and
 *     airtight** — no fetch is needed, because the read succeeding *is* the
 *     proof.
 *   - **`workspace_visible`** — arm (b) (a workspace admin, same proof as
 *     above) or arm (c) (an ordinary workspace member, admitted to *read* by
 *     (c) unconditionally, and to *write* by `requireBoardWriter` exactly
 *     while `selfJoinEnabled` is true — the default). `FR-PERM-009`: workspace
 *     members "may also join it and edit it without an invitation." **True**,
 *     carrying the pre-existing self-join residual (residual 1 below).
 *   - **`public`** — arm (d) admits the entire signed-in *and* signed-out
 *     internet, unconditionally. A successful read proves nothing about
 *     `workspaceRole`: the viewer could be a stranger, or could (rarely) be a
 *     workspace admin of this board's own workspace who simply followed the
 *     public link instead of an internal one. Nothing here distinguishes the
 *     two, so this branch must answer for the population it **cannot** rule
 *     out being wrong about — a stranger — over the population it could
 *     rarely be short-changing. `FR-PERM-010`: "Editing remains restricted to
 *     board members — visibility never grants write access." **False.**
 *   - **`enterprise_visible`** — the enterprise arm, same reasoning as (c) vs.
 *     (d): it admits a member of a *sibling* workspace in the same Enterprise,
 *     unconditionally, so a successful read proves nothing about this board's
 *     *own* workspace role either. **False**, same shape as `public`.
 *
 * `private` and `workspace_visible` both fall on the "arm (b) is workspace-
 * admin-or-narrower" side and return `true`; `public` and `enterprise_visible`
 * both fall on the "an unconditional arm admits strangers" side and return
 * `false`. That pairing — not "three values true, one false" or any other
 * split — is what the boolean expression encodes, and it is why `private` is
 * grouped with `workspace_visible` rather than left `false` as the safer-
 * looking default: leaving it `false` would have been a **regression** from
 * `main`, where `canEdit` is `!board.closed` alone and a workspace admin
 * viewing a private board with no board row can edit today.
 *
 * No extra fetch discriminates any of this — none is added. `board.visibility`
 * is already on `BoardContentsBoard` (`boardContentsBoardSchema` extends
 * `boardSchema`), and that is deliberate: `packages/shared/src/board.ts`
 * carries no per-viewer capability field on a per-board payload, by design
 * (`D4-BRIEF.md` §3.3) — this predicate is the client-side stand-in for that,
 * computed once here rather than re-derived per affordance.
 *
 * ---------------------------------------------------------------------------
 * THE FAIL-OPEN ASYMMETRY — AND WHY `boardCapabilities` (`lib/boards.ts`)
 * DELIBERATELY DOES THE OPPOSITE
 * ---------------------------------------------------------------------------
 * `boardCapabilities` fails **closed** when its own `rolesKnown` is false — an
 * incomplete derivation must not enable a destructive, irreversible control
 * (close, delete). `canEdit` fails **open**. This is not an inconsistency to
 * tidy; the two guards protect against opposite mistakes because their wrong
 * answers cost different things:
 *
 *   - wrong-**open** on `canEdit`: an affordance renders for a viewer who
 *     turns out not to be able to use it, the mutation 403s, and the rollback
 *     the codebase already ships (`announceRollback`) undoes it.
 *   - wrong-**closed** on `canEdit`: on a **private board opened by its own
 *     admin** — the commonest case in the product — `rolesKnown` is false for
 *     the width of the members query, `role` is `null`, and `visibility` is
 *     `'private'`. A fail-closed rule would render the *entire board*
 *     read-only at first paint, for its own admin, and then pop editable once
 *     the members query settles. That is a visible regression from the old
 *     `!board.closed` behaviour, and it is worse than the 403 the other
 *     direction risks.
 *
 * Both defaults are correct for the guard that carries them. Changing either
 * to match the other reintroduces the failure mode it was chosen to avoid.
 *
 * ---------------------------------------------------------------------------
 * THE RESIDUALS — REPORTED, NOT BUILT FOR
 * ---------------------------------------------------------------------------
 * Three, and they are not interchangeable — direction and novelty matter.
 *
 * 1. **Wrong-open, pre-existing, unchanged by `D4`.** A `workspace_visible`
 *    board whose admin has turned **self-join off** (`FR-PERM-028`, the
 *    toggle is `FE-38.5`'s and defaults **on**) still shows content
 *    affordances to an ordinary workspace member holding no `board_members`
 *    row — this predicate has no way to see that toggle, because no route
 *    this file calls exposes it, and none is to be added (`GET /workspaces`,
 *    `GET /boards/:id/self-join`) to refine this one narrow case. The cost
 *    when it fires is a 403 on the attempted write plus the rollback
 *    announcement the codebase already ships — not a silent failure and not
 *    data loss.
 * 2. **Wrong-closed, and newly introduced by `D4`.** A workspace admin
 *    holding no `board_members` row, on a `public` or `enterprise_visible`
 *    board **of their own workspace**, can edit today (`canEdit` on `main` is
 *    `!board.closed` alone) and cannot after this chunk — branch 6 must
 *    return `false` for `public`/`enterprise_visible` because it cannot tell
 *    that rare workspace-admin viewer apart from an ordinary stranger the
 *    same arm admits (see "WHY BRANCH 6 IS WHAT IT IS" above). This is
 *    unknowable client-side: `workspaceRole` appears on no board response —
 *    the same missing-per-viewer-capability-field gap `D4-BRIEF.md` §3.3
 *    reports for the fourth time, now on its fifth. `D4` ships this knowingly
 *    rather than adding the forbidden fetch.
 * 3. **Wrong-closed, narrow, independent of branch 6.** An observer who is
 *    *also* a workspace admin. `requireBoardWriter`'s observer refusal is
 *    `boardRole === 'observer' && workspaceRole !== 'admin'`, so it admits
 *    this person server-side; client-side, branch 4 (`role === 'observer'`)
 *    fires first and returns `false` unconditionally, before `workspaceRole`
 *    could ever be consulted. No reordering of the six branches fixes this —
 *    it needs a `workspaceRole` this predicate does not have. Documented, not
 *    built.
 *
 * ---------------------------------------------------------------------------
 * WHAT THIS FILE DOES NOT DO
 * ---------------------------------------------------------------------------
 * `canEdit` takes exactly these two arguments. No overload, no default
 * `viewer`, no second exported name — a default parameter would let a caller
 * silently keep the pre-`D4` meaning, which is the bug this widening exists to
 * close. Every consumer on the board face computes `viewer` once, in the
 * route (`b.$shortLink.{-$slug}.tsx`), and gates on the boolean this function
 * returns; none of them branches on `board.closed`, `viewer.role` or
 * `board.visibility` directly.
 */
export function canEdit(board: BoardContentsBoard, viewer: BoardViewer): boolean {
  if (board.closed) return false;
  if (!viewer.signedIn) return false;
  if (viewer.isSuper) return true;
  if (!viewer.rolesKnown) return true;
  if (viewer.role === 'observer') return false;
  if (viewer.role !== null) return true;
  // `role === null` — the read itself is evidence of standing, and which
  // evidence depends on which arm of the read scope admitted this viewer.
  return board.visibility === 'private' || board.visibility === 'workspace_visible';
}

/**
 * The path for a card the board face builds itself — the calendar, timeline,
 * map and table rows, which carry no `path` of their own
 * (`docs/notes/card-keys.md`).
 *
 * ⚠ It must be the path the server builds for THIS viewer, byte for byte,
 * because `FR-BOARD-023`'s card-return state is keyed on the path string and a
 * one-byte mismatch silently loses the scroll position on close. So it goes
 * through the shared `cardPath`/`cardRefFor` the server uses, and it puts the
 * board's key in the path only when `cardRefsResolve` says this viewer may
 * open a reference — a public-arm or anonymous reader gets the short link,
 * because a reference would 404 on their next reload. `board.cardKey` alone
 * is the board's key, not permission to use it.
 *
 * The slug is `slugify(card.title)` over the RAW title, never the display
 * fallback (`Untitled card`), which is what the server slugifies too.
 */
export function boardViewCardPath(
  board: Pick<BoardContentsBoard, 'cardKey' | 'cardRefsResolve'>,
  card: {
    readonly shortLink: string;
    readonly cardNumber: number;
    readonly title: string;
  },
): string {
  return cardPath(
    {
      ref: cardRefFor(board.cardRefsResolve ? board.cardKey : null, card.cardNumber),
      shortLink: card.shortLink,
    },
    slugify(card.title),
  );
}

/**
 * A card path from a projection row whose `ref` the SERVER already scoped to
 * this viewer — the reference when they may open one, else `null` and the
 * short link. The workspace calendar and table rows carry it this way.
 *
 * Takes the row whole, so a call site has no field of its own to drop.
 */
export function viewerCardPath(card: {
  readonly ref: string | null;
  readonly shortLink: string;
  readonly title: string;
}): string {
  return cardPath({ ref: card.ref, shortLink: card.shortLink }, slugify(card.title));
}
